import { DirectionalHint, IconButton, mergeStyles, Stack, TooltipHost } from '@fluentui/react';
import { IC } from 'components/SVGIcon';
import React from 'react';
import { useRecoilState } from 'recoil';
import { aIsAudioDeviceSetVisible } from 'routes/LandingPage/atom';
import { useShowAttachmentPanel, useWindowWidth } from 'shared-state/display-options/hooks';
import { useIsVisitorAtLandingPage } from 'shared-state/location/hook';
import { ICallActionButtonProps } from './CallActionButton';

const CallActionButtonView: React.FC<ICallActionButtonProps> = ({
    action,
    onPressed,
    micIsMuted,
    cameraIsMuted,
    sharingIsOn,
    statsAreVisible,
    isLocalUserHandRaised,
    isChatVisible
}) => {
    const [showAttachmentPanel] = useShowAttachmentPanel();

    const iconButtonStylesSelected = mergeStyles({
        borderRadius: 4,
        padding: 8,
        position: 'relative',
        cursor: 'pointer',
        userSelect: 'none',
        border: '1px solid var(--stroke-gray-100, #C9CAC8)',
        background: 'var(--stroke-gray-50, #E4E4E3)',
        boxShadow: '4px 4px 4px 0px rgba(0, 0, 0, 0.25) inset',
        selectors: {
            ':hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                border: '1px solid var(--stroke-gray-100, #C9CAC8)'
            },
            ':active': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                border: '1px solid var(--stroke-gray-100, #C9CAC8)'
            }
        }
    });

    const iconButtonStylesDefault = mergeStyles({
        borderRadius: 4,
        padding: 8,
        position: 'relative',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        userSelect: 'none',
        border: '1px solid transparent',
        selectors: {
            ':hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                border: '1px solid transparent'
            },
            ':active': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                border: '1px solid transparent'
            }
        }
    });

    const iconButtonStylesDisabled = mergeStyles({
        borderRadius: 4,
        padding: 8,
        position: 'relative',
        backgroundColor: 'transparent',
        cursor: 'not-allowed',
        userSelect: 'none',
        border: '1px solid transparent',
        opacity: 0.5,
        selectors: {
            ':hover': {
                backgroundColor: 'transparent',
                border: '1px solid transparent'
            },
            ':active': {
                backgroundColor: 'transparent',
                border: '1px solid transparent'
            }
        }
    });

    const getButtonStyles = (actionKey: string, micIsMuted?: boolean, cameraIsMuted?: boolean, sharingIsOn?: boolean) => {
        // Show hand raise button as disabled when hand is raised (modal is showing)
        if (actionKey === 'raise-hand' && isLocalUserHandRaised) {
            return iconButtonStylesDisabled; // Use disabled style
        }

        if (
            (actionKey === 'toggle-audio' && !micIsMuted) ||
            (actionKey === 'toggle-video' && !cameraIsMuted) ||
            (actionKey === 'share-screen' && sharingIsOn) ||
            (actionKey === 'toggle-stats' && statsAreVisible) ||
            (actionKey === 'toggle-link-attachments' && showAttachmentPanel === 'link') ||
            (actionKey === 'toggle-tool-attachments' && showAttachmentPanel === 'tool') ||
            (actionKey === 'toggle-chat' && isChatVisible)
        ) {
            return iconButtonStylesSelected;
        }
        return iconButtonStylesDefault;
    };

    const { smallDevice } = useWindowWidth();
    const [isVisitorAtLandingPage] = useIsVisitorAtLandingPage();
    const [, setIsAudioDeviceSetVisible] = useRecoilState<boolean>(aIsAudioDeviceSetVisible);

    const handleClick = (providedAction) => {
        const { key } = providedAction;
        // Disable hand raise button when hand is already raised (modal is showing)
        if (key === 'raise-hand' && isLocalUserHandRaised) {
            return;
        }
        if (key === 'device-settings' && isVisitorAtLandingPage) {
            setIsAudioDeviceSetVisible(false);
        }
        onPressed(providedAction);
    };

    const contentCondition = !smallDevice ? action.title : '';

    return (
        <>
            <TooltipHost content={contentCondition} directionalHint={DirectionalHint.bottomCenter} hidden={false} calloutProps={{ gapSpace: 5 }}>
                <>
                    <IconButton
                        className={getButtonStyles(action.key, micIsMuted, cameraIsMuted, sharingIsOn)}
                        key={action.key}
                        onClick={() => handleClick(action)}
                        styles={{ root: { minWidth: '40px', marginTop: 4, marginBottom: 4 } }}
                        data-test-id={action.key + '-action'}
                        onRenderIcon={() =>
                            isVisitorAtLandingPage ? (
                                <Stack className={'hoverable'} horizontal horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 6 }}>
                                    <IC variant={action.iconVariant || 'dark'} size={24}>
                                        {action.icon}
                                    </IC>
                                </Stack>
                            ) : (
                                <Stack className={'hoverable'} verticalAlign="center" horizontalAlign="center" style={{ maxWidth: '40px' }}>
                                    <IC variant={action.iconVariant || 'dark'} size={24}>
                                        {action.icon}
                                    </IC>
                                </Stack>
                            )
                        }
                    />
                </>
            </TooltipHost>
        </>
    );
};

export default CallActionButtonView;
